import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  ApBulkUploadResponse,
  ApDocumentType,
  ApUploadInvoiceResponse,
} from "@/accounts-payable/types";
import { useApi } from "@/core/api";
import { AP_DOCUMENT_ENDPOINTS, reviewQueueKey } from "./keys";
import { markInvoiceUpload } from "./polling";

export const useUploadInvoices = (businessId?: string | null) =>
  useUploadDocuments(businessId, "invoice");

export const useUploadDocuments = (
  businessId: string | null | undefined,
  documentType: ApDocumentType,
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const endpoints = AP_DOCUMENT_ENDPOINTS[documentType];
  return useMutation({
    mutationFn: async (files: File[]): Promise<ApBulkUploadResponse> => {
      const [single] = files;
      if (single && files.length === 1) {
        const formData = new FormData();
        formData.append("file", single, single.name);
        const { data } = await api.post<ApUploadInvoiceResponse>(
          `/businesses/${businessId}/ap/${endpoints.path}`,
          formData,
        );
        return {
          message: data.message,
          queued: [
            { file_name: single.name, attachment_id: data.attachment_id },
          ],
          skipped: [],
        };
      }

      const formData = new FormData();
      files.forEach((file) => formData.append("files[]", file, file.name));
      return api
        .post<ApBulkUploadResponse>(
          `/businesses/${businessId}/ap/${endpoints.path}/bulk_upload`,
          formData,
        )
        .then((response) => response.data);
    },
    onSuccess: () => {
      markInvoiceUpload();
      void queryClient.invalidateQueries({
        queryKey: endpoints.listKey(businessId),
      });
      void queryClient.invalidateQueries({
        queryKey: reviewQueueKey(businessId),
      });
    },
  });
};
